import { Setting, TimeOfDay } from '../services/types';

export type SceneColorKey = `${Setting}:${TimeOfDay}`;
export type SceneColorPalette = Partial<Record<SceneColorKey, string>>;
export const SCENE_COLOR_SETTINGS = Object.freeze(Object.values(Setting));
export const SCENE_COLOR_TIMES = Object.freeze(Object.values(TimeOfDay));
export const sceneColorKey = (setting: Setting, time: TimeOfDay): SceneColorKey => `${setting}:${time}`;

// Classic stripboard convention (Movie Magic 5 manual, p.100):
// INT/DAY white, EXT/DAY yellow, INT/NIGHT blue, EXT/NIGHT green.
// Dawn/morning use pink; dusk/evening use orange. Mixed INT/EXT follows EXT.
// These readable screen shades are our palette, not claimed vendor HEX values.
export const STANDARD_SCENE_COLORS = Object.freeze({
    interiorDay: '#FFFFFF', exteriorDay: '#FFFF80',
    interiorNight: '#A6C8FF', exteriorNight: '#B8E6B8',
    morning: '#F5B6D2', evening: '#F6C16B', unset: '#C5C8CB',
});
export const getStandardSceneColor = (setting?: Setting, time?: TimeOfDay): string => {
    if (time === TimeOfDay.DAWN || time === TimeOfDay.MORNING) return STANDARD_SCENE_COLORS.morning;
    if (time === TimeOfDay.DUSK || time === TimeOfDay.EVENING) return STANDARD_SCENE_COLORS.evening;
    if (!setting) return STANDARD_SCENE_COLORS.unset;
    if (time === TimeOfDay.DAY) return setting === Setting.INT ? STANDARD_SCENE_COLORS.interiorDay : STANDARD_SCENE_COLORS.exteriorDay;
    if (time === TimeOfDay.NIGHT) return setting === Setting.INT ? STANDARD_SCENE_COLORS.interiorNight : STANDARD_SCENE_COLORS.exteriorNight;
    return STANDARD_SCENE_COLORS.unset;
};
export const isSceneColor = (value: unknown): value is string => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
export const normalizeSceneColorPalette = (value: unknown): SceneColorPalette => {
    const result: SceneColorPalette = {};
    if (!value || typeof value !== 'object' || Array.isArray(value)) return result;
    const source = value as Record<string, unknown>;
    SCENE_COLOR_SETTINGS.forEach(setting => SCENE_COLOR_TIMES.forEach(time => {
        const key = sceneColorKey(setting, time);
        const color = source[key];
        if (isSceneColor(color)) result[key] = color.toUpperCase();
    }));
    return result;
};

/** Black or white guarantees readable text (at least WCAG AA) on a solid RGB fill. */
export const sceneColorText = (color: string): string => {
    const rgb = [1, 3, 5].map(offset => parseInt(color.slice(offset, offset + 2), 16) / 255)
        .map(channel => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    const luminance = rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
    return luminance > 0.179 ? '#000000' : '#FFFFFF';
};
